import { FC } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { colors } from '@packages/core/styles';
import { AutosaveStatus } from './hooks/use-autosave';
import { BLOCKED_REASON } from './autosave.utils';

interface AutosaveIndicatorProps {
    status: AutosaveStatus;
    savedAt?: Date;
    errorMessage?: string;
    blockedReason: string | null;
    fileNote?: string | null;
    onRetry: () => void;
}

const useStyles = createUseStyles({
    indicator: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minHeight: 40,
        marginRight: 8,
        whiteSpace: 'nowrap',
    },
    publishBlocked: {
        padding: '0 10px',
        borderRadius: 4,
        backgroundColor: colors.redFour,
        animation: '$shake 0.5s ease-in-out, $flash 1.2s ease-out',
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
        },
    },
    '@keyframes shake': {
        '0%, 100%': { transform: 'translateX(0)' },
        '20%, 60%': { transform: 'translateX(-6px)' },
        '40%, 80%': { transform: 'translateX(6px)' },
    },
    '@keyframes flash': {
        '0%': { backgroundColor: colors.redThree },
        '100%': { backgroundColor: colors.redFour },
    },
});

const formatSavedAt = (savedAt: Date) =>
    savedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

export const AutosaveIndicator: FC<AutosaveIndicatorProps> = ({
    status,
    savedAt,
    errorMessage,
    blockedReason,
    fileNote,
    onRetry,
}) => {
    const classes = useStyles();

    const note = fileNote ? (
        <Typography variant="caption" color="graySix">
            {`· ${fileNote}`}
        </Typography>
    ) : null;

    if (status === 'saving') {
        return (
            <div className={classes.indicator} role="status" aria-live="polite">
                <Typography variant="caption" color="graySix">
                    Saving…
                </Typography>
                {note}
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className={classes.indicator} role="alert">
                <Icon name="error" color="redOne" size={18} />
                <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id="autosave-error-tooltip">{errorMessage || 'Please try again'}</Tooltip>}
                >
                    <span>
                        <Typography variant="caption" color="redOne">
                            Couldn't save
                        </Typography>
                    </span>
                </OverlayTrigger>
                <Button variant="link" onPress={onRetry}>
                    Retry
                </Button>
            </div>
        );
    }

    if (blockedReason === BLOCKED_REASON.missingRequiredForPublished) {
        return (
            <div className={`${classes.indicator} ${classes.publishBlocked}`} role="alert">
                <Icon name="error" color="redOne" size={18} />
                <Typography variant="caption" color="redOne">
                    {blockedReason}
                </Typography>
            </div>
        );
    }

    if (blockedReason) {
        return (
            <div className={classes.indicator} role="status" aria-live="polite">
                <Icon name="info" color="graySix" size={18} />
                <Typography variant="caption" color="graySix">
                    {blockedReason}
                </Typography>
            </div>
        );
    }

    if (status === 'saved' && savedAt) {
        return (
            <div className={classes.indicator} role="status" aria-live="polite">
                <Icon name="checkmark" color="greenOne" size={18} />
                <Typography variant="caption" color="graySix">
                    {`Auto saved at ${formatSavedAt(savedAt)}`}
                </Typography>
                {note}
            </div>
        );
    }

    if (fileNote) {
        return (
            <div className={classes.indicator} role="status" aria-live="polite">
                <Icon name="info" color="graySix" size={18} />
                <Typography variant="caption" color="graySix">
                    {fileNote}
                </Typography>
            </div>
        );
    }

    return null;
};
